import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApi } from '../../services/api';
import { Game, BingoItem, GameStatus } from '@bst-stellar/shared';
import { formatDate, formatDateTime } from '../../utils/dateFormatter';
import BingoCard from '../../components/BingoCard';

export default function GameDetails() {
    const { gameId } = useParams<{ gameId: string }>();
    const api = useApi();

    const [game, setGame] = useState<Game | null>(null);
    const [participants, setParticipants] = useState<any[]>([]);
    const [claims, setClaims] = useState<any[]>([]);
    const [items, setItems] = useState<BingoItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [participantsLoading, setParticipantsLoading] = useState(true);
    const [expandedCard, setExpandedCard] = useState<string | null>(null);
    const [ending, setEnding] = useState(false);

    useEffect(() => {
        if (gameId) {
            loadGameDetails();
        }
    }, [gameId]);

    async function loadGameDetails() {
        try {
            setLoading(true);
            const [gameResult, claimsResult, itemsResult] = await Promise.all([
                api.getGame(gameId!),
                api.getClaims(),
                api.getItems(),
            ]);

            setGame(gameResult.game);
            setItems(itemsResult.items || []);

            // Filter claims for this game
            const gameClaims = claimsResult.claims?.filter((claim: any) => {
                return claim.gameId === gameId;
            }) || [];
            setClaims(gameClaims);

            // Load participants
            loadParticipants();
        } catch (error) {
            console.error('Failed to load game details:', error);
        } finally {
            setLoading(false);
        }
    }

    async function loadParticipants() {
        try {
            setParticipantsLoading(true);
            const result = await api.getGameParticipants(gameId!);
            setParticipants(result.participants || []);
        } catch (error) {
            console.error('Failed to load participants:', error);
            setParticipants([]);
        } finally {
            setParticipantsLoading(false);
        }
    }

    async function handleEndGame() {
        if (!window.confirm('Are you sure you want to end this game? This will stop all new submissions from employees.')) {
            return;
        }
        try {
            setEnding(true);
            await api.updateGame(gameId!, { status: GameStatus.COMPLETED });
            await loadGameDetails();
        } catch (error) {
            console.error('Failed to end game:', error);
            alert('Failed to end the game. Please try again.');
        } finally {
            setEnding(false);
        }
    }

    if (loading) {
        return <div className="text-center py-12">Loading game details...</div>;
    }

    if (!game) {
        return <div className="text-center py-12 text-red-600">Game not found</div>;
    }

    const approvedClaims = claims.filter(c => c.status === 'approved');
    const pendingClaims = claims.filter(c => c.status === 'pending');
    const canEndGame = game.status !== GameStatus.COMPLETED;

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold">{game.name}</h1>
                <div className="flex items-center gap-4">
                    {canEndGame && (
                        <button
                            onClick={handleEndGame}
                            disabled={ending}
                            className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50"
                        >
                            {ending ? 'Ending...' : '🛑 End Game'}
                        </button>
                    )}
                    <Link
                        to={`/admin/games/${gameId}/edit`}
                        className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                    >
                        Edit Game
                    </Link>
                    <Link
                        to="/admin/games"
                        className="text-blue-600 hover:text-blue-800"
                    >
                        ← Back to Games
                    </Link>
                </div>
            </div>

            {/* Game Info */}
            <div className="bg-surface rounded-lg shadow p-6 mb-6 border border-border">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                        <p className="text-sm text-gray-500">Status</p>
                        <p className="font-semibold capitalize">{game.status}</p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">Start Date</p>
                        <p className="font-semibold">{formatDate(game.startDate)}</p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">End Date</p>
                        <p className="font-semibold">{formatDate(game.endDate)}</p>
                    </div>
                </div>
                {game.description && (
                    <div className="mt-4">
                        <p className="text-sm text-gray-500">Description</p>
                        <p className="mt-1">{game.description}</p>
                    </div>
                )}
                {game.rewardBanner && (
                    <div className="mt-4">
                        <p className="text-sm text-gray-500">🏆 Reward</p>
                        <p className="mt-1">{game.rewardBanner}</p>
                    </div>
                )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-surface rounded-lg shadow p-6 border border-border">
                    <div className="text-sm text-text-secondary mb-1">Total Claims</div>
                    <div className="text-3xl font-bold text-primary">{claims.length}</div>
                </div>
                <div className="bg-surface rounded-lg shadow p-6 border border-border">
                    <div className="text-sm text-text-secondary mb-1">Approved</div>
                    <div className="text-3xl font-bold text-success">{approvedClaims.length}</div>
                </div>
                <div className="bg-surface rounded-lg shadow p-6 border border-border">
                    <div className="text-sm text-text-secondary mb-1">Pending</div>
                    <div className="text-3xl font-bold text-warning">{pendingClaims.length}</div>
                </div>
                <div className="bg-surface rounded-lg shadow p-6 border border-border">
                    <div className="text-sm text-text-secondary mb-1">Participants</div>
                    <div className="text-3xl font-bold text-accent">{participants.length}</div>
                </div>
            </div>

            {/* Participants Section */}
            <div className="bg-surface rounded-lg shadow border border-border mb-6">
                <div className="p-6 border-b border-border">
                    <h2 className="text-xl font-semibold">👥 Participants ({participants.length})</h2>
                </div>
                <div className="divide-y divide-border">
                    {participantsLoading ? (
                        <div className="p-8 text-center text-text-muted">
                            Loading participants...
                        </div>
                    ) : participants.length === 0 ? (
                        <div className="p-8 text-center text-text-muted">
                            No participants yet
                        </div>
                    ) : (
                        participants.map((participant) => (
                            <div key={participant.userId} className="p-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        {/* Rank Badge */}
                                        <div className={`w-8 h-8 flex items-center justify-center rounded-full font-bold text-sm ${participant.rank === 1 ? 'bg-yellow-100 text-yellow-800' :
                                            participant.rank === 2 ? 'bg-gray-200 text-gray-700' :
                                                participant.rank === 3 ? 'bg-orange-100 text-orange-800' :
                                                    'bg-gray-100 text-gray-600'
                                            }`}>
                                            #{participant.rank}
                                        </div>
                                        {/* User Info */}
                                        <div>
                                            <p className="font-medium text-text-primary">{participant.userName}</p>
                                            <p className="text-sm text-text-muted">{participant.userEmail}</p>
                                        </div>
                                    </div>
                                    {/* Stats */}
                                    <div className="flex items-center gap-6">
                                        <div className="text-center">
                                            <div className="text-lg font-bold text-primary">{participant.score}</div>
                                            <div className="text-xs text-text-muted">points</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-lg font-bold text-text-primary">{participant.completedSquares}</div>
                                            <div className="text-xs text-text-muted">squares</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-lg font-bold text-green-600">{participant.bingoLines}</div>
                                            <div className="text-xs text-text-muted">bingos</div>
                                        </div>
                                        {/* Mini Card Grid Toggle */}
                                        <button
                                            onClick={() => setExpandedCard(
                                                expandedCard === participant.userId ? null : participant.userId
                                            )}
                                            className="text-sm text-primary hover:underline"
                                        >
                                            {expandedCard === participant.userId ? 'Hide Card' : 'Show Card'}
                                        </button>
                                    </div>
                                </div>
                                {/* Expanded Card View */}
                                {expandedCard === participant.userId && (
                                    <div className="mt-4 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg overflow-x-auto">
                                        <p className="text-sm text-text-muted mb-3">Card Progress:</p>
                                        <BingoCard
                                            gridData={participant.gridData}
                                            items={items}
                                            claims={participant.claims}
                                            onClaimClick={() => { }}
                                            readOnly={true}
                                        />
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Recent Claims */}
            <div className="bg-surface rounded-lg shadow border border-border">
                <div className="p-6 border-b border-border">
                    <h2 className="text-xl font-semibold">Recent Claims</h2>
                </div>
                <div className="divide-y divide-border">
                    {claims.length === 0 ? (
                        <div className="p-8 text-center text-text-muted">
                            No claims yet
                        </div>
                    ) : (
                        claims.slice(0, 10).map((claim) => (
                            <div key={claim.id} className="p-4 hover:bg-surface/80">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <p className="font-medium text-text-primary">{claim.description || 'No description'}</p>
                                        <p className="text-sm text-text-muted mt-1">
                                            {formatDateTime(claim.createdAt)}
                                        </p>
                                    </div>
                                    <span className={`px-2 py-1 text-xs font-semibold rounded ${claim.status === 'approved' ? 'bg-green-100 text-green-800' :
                                        claim.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                            'bg-red-100 text-red-800'
                                        }`}>
                                        {claim.status}
                                    </span>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}
